import { useState, useEffect, useMemo, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';

export interface PermissionChild {
  id: number;
  name: string;
  permission: string;
}

export interface PermissionCategory {
  id: number;
  name: string;
  children: PermissionChild[];
}

export interface Person {
  id: number;
  name: string;
}

export interface CreateCustomGroupSaveData {
  title: string;
  permissions: string[];
  people: number[];
}

export interface CreateCustomGroupModalProps {
  open: boolean;
  categories?: PermissionCategory[];
  people?: Person[];
  existingGroupTitles?: string[];
  maxTitleLength?: number;
  theme?: string;
  onSave?: (data: CreateCustomGroupSaveData) => void;
  onCancel: () => void;
}

export function CreateCustomGroupModal({
  open,
  categories = [],
  people = [],
  existingGroupTitles = [],
  maxTitleLength = 50,
  theme,
  onSave,
  onCancel,
}: CreateCustomGroupModalProps) {
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, boolean>>({});
  const [selectedPeople, setSelectedPeople] = useState<number[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<number, boolean>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setTitleError('');
      setSelectedPermissions({});
      setSelectedPeople([]);
      // Expand first category by default
      const initial: Record<number, boolean> = {};
      if (categories.length > 0) {
        initial[categories[0].id] = true;
      }
      setExpandedCategories(initial);
    }
  }, [open]);

  const charCount = useMemo(
    () => title.length + '/' + maxTitleLength,
    [title, maxTitleLength]
  );

  const sortedPeople = useMemo(
    () => [...people].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [people]
  );

  const selectedPermissionCount = useMemo(() => {
    let count = 0;
    for (const key in selectedPermissions) {
      if (selectedPermissions[key]) count++;
    }
    return count;
  }, [selectedPermissions]);

  const selectedPeopleCount = useMemo(
    () => selectedPeople.length,
    [selectedPeople]
  );

  const getCategorySelectedCount = useCallback(
    (category: PermissionCategory): number => {
      const children = category.children || [];
      let count = 0;
      for (let i = 0; i < children.length; i++) {
        if (children[i].permission && selectedPermissions[children[i].permission]) {
          count++;
        }
      }
      return count;
    },
    [selectedPermissions]
  );

  const validateTitle = useCallback((): boolean => {
    const trimmed = title.trim();

    if (!trimmed) {
      setTitleError('Group name is required.');
      return false;
    }

    if (trimmed.length > maxTitleLength) {
      setTitleError('Group name must be ' + maxTitleLength + ' characters or fewer.');
      return false;
    }

    const lower = trimmed.toLowerCase();
    for (let i = 0; i < existingGroupTitles.length; i++) {
      if (existingGroupTitles[i].toLowerCase() === lower) {
        setTitleError('A group with this name already exists.');
        return false;
      }
    }

    setTitleError('');
    return true;
  }, [title, maxTitleLength, existingGroupTitles]);

  const handleSave = useCallback(() => {
    if (!validateTitle()) return;

    const permissions: string[] = [];
    for (const key in selectedPermissions) {
      if (selectedPermissions[key]) {
        permissions.push(key);
      }
    }

    onSave?.({
      title: title.trim(),
      permissions,
      people: [...selectedPeople],
    });
  }, [validateTitle, selectedPermissions, title, selectedPeople, onSave]);

  const toggleCategory = useCallback((categoryId: number) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  const togglePermission = useCallback((permission: string) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  }, []);

  const togglePerson = useCallback((personId: number) => {
    setSelectedPeople((prev) => {
      const index = prev.indexOf(personId);
      if (index > -1) {
        const next = prev.slice();
        next.splice(index, 1);
        return next;
      }
      return [...prev, personId];
    });
  }, []);

  const selectAllInCategory = useCallback((category: PermissionCategory) => {
    const children = category.children || [];
    setSelectedPermissions((prev) => {
      const next = { ...prev };
      for (let i = 0; i < children.length; i++) {
        if (children[i].permission) {
          next[children[i].permission] = true;
        }
      }
      return next;
    });
  }, []);

  const clearAllInCategory = useCallback((category: PermissionCategory) => {
    const children = category.children || [];
    setSelectedPermissions((prev) => {
      const next = { ...prev };
      for (let i = 0; i < children.length; i++) {
        if (children[i].permission) {
          next[children[i].permission] = false;
        }
      }
      return next;
    });
  }, []);

  const selectAllPeople = useCallback(() => {
    setSelectedPeople(sortedPeople.map((p) => p.id));
  }, [sortedPeople]);

  const clearAllPeople = useCallback(() => {
    setSelectedPeople([]);
  }, []);

  return (
    <BaseFormModal
      open={open}
      title="Create Custom Permission Group"
      theme={theme}
      dialogClass="create-custom-group-modal"
      saveText="Create Group"
      cancelText="Cancel"
      scrollLock={true}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="modal-form">
        {/* Group Name */}
        <div className="form-group full-width">
          <label>
            Group Name <span className="required">*</span>
          </label>
          <input
            type="text"
            value={title}
            maxLength={maxTitleLength}
            placeholder="Enter group name..."
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            className={'form-input' + (titleError ? ' has-error' : '')}
          />
          <div className="input-footer">
            {titleError && <span className="error-message">{titleError}</span>}
            <span className="char-count">{charCount}</span>
          </div>
        </div>

        {/* Permissions Section */}
        <div className="form-group full-width">
          <div className="section-header">
            <label>Permissions</label>
            <span className="section-count">{selectedPermissionCount} selected</span>
          </div>
          <div className="permissions-list">
            {categories.map((category) => (
              <div key={category.id} className="permission-category">
                <div
                  className="category-header"
                  onClick={() => toggleCategory(category.id)}
                >
                  <i
                    className={
                      'fa ' +
                      (expandedCategories[category.id]
                        ? 'fa-chevron-down'
                        : 'fa-chevron-right')
                    }
                  ></i>
                  <span className="category-name">{category.name}</span>
                  <span className="category-count">
                    {getCategorySelectedCount(category)}/{category.children.length}
                  </span>
                  <div
                    className="category-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="btn-mini"
                      onClick={() => selectAllInCategory(category)}
                      title="Select all"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className="btn-mini"
                      onClick={() => clearAllInCategory(category)}
                      title="Clear all"
                    >
                      None
                    </button>
                  </div>
                </div>
                {expandedCategories[category.id] && (
                  <div className="category-permissions">
                    {category.children.map((child) => (
                      <label key={child.id} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={!!selectedPermissions[child.permission]}
                          onChange={() => togglePermission(child.permission)}
                        />
                        <span>{child.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* People Section */}
        <div className="form-group full-width">
          <div className="section-header">
            <label>Assign Users</label>
            <span className="section-count">{selectedPeopleCount} selected</span>
            <div className="section-actions">
              <button
                type="button"
                className="btn-mini"
                onClick={selectAllPeople}
              >
                All
              </button>
              <button
                type="button"
                className="btn-mini"
                onClick={clearAllPeople}
              >
                None
              </button>
            </div>
          </div>
          <div className="people-list">
            {sortedPeople.map((person) => (
              <label key={person.id} className="person-checkbox">
                <input
                  type="checkbox"
                  checked={selectedPeople.indexOf(person.id) > -1}
                  onChange={() => togglePerson(person.id)}
                />
                <span>{person.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </BaseFormModal>
  );
}

(window as any).CreateCustomGroupModalReact = CreateCustomGroupModal;
